import React, { FC } from 'react';

import { Money as MoneyModel, PrimaryMarketProject, Reservation, StatusKey } from 'api-client';
import { CustomLink, DataTable, Money, TertiaryButton } from 'components/common';
import { TertiaryButtonColor } from 'components/common/buttons/decorators/TertiaryButton/TertiaryButton';
import { CheckIcon } from 'components/common/icons';
import { ProjectDebtClass } from 'components/project';
import { formatDate } from 'formatters';
import { useProjectRowClick } from 'hooks';
import { RouteDictionary } from 'utils';

import { ymAsync } from '../../../../../utils/functions/ymWrapper';
import { usePrimaryMarketProjectContent } from '../usePrimaryMarketProjectContent';
import styles from './PrimaryMarketTableRow.module.scss';

interface Props {
  project: PrimaryMarketProject;
  statusKey: StatusKey;
  reservation?: Reservation;
}

const PrimaryMarketTableRow: FC<Props> = ({ project, statusKey, reservation }) => {
  const {
    devAndTest,
    showInvestButton,
    isFullyCollected,
    roiIrr,
    progressBar,
    factTerm,
    plannedCompletion,
    startedAt,
    completedAt,
    cancelledAt,
  } = usePrimaryMarketProjectContent(project);
  const isPaidTable = statusKey === 'Завершен';
  const isCurrentTable = statusKey === 'Текущий';
  const isRefundedTable = statusKey === 'Отменен';
  // У этих таблиц даты вынесены в отдельные колонки, а прогресс-бар стоит последним.
  const withDateColumns = isCurrentTable || isPaidTable || isRefundedTable;
  const handleRowClick = useProjectRowClick(project.uuid, () => ymAsync('click-to-project-card'));

  const renderInvestAction = () => (
    <div className={styles.action}>
      <TertiaryButton expanded color={TertiaryButtonColor.GREEN} className={styles.investButton}>
        <CustomLink
          className={styles.investLink}
          to={RouteDictionary.DETAILED_PROJECT}
          params={{ uuid: project.uuid }}
          onClick={() => ymAsync('invest-all-projects-button')}
        >
          {devAndTest ? 'Подать заявку' : 'Инвестировать'}
        </CustomLink>
      </TertiaryButton>
      <span className={styles.actionHint}>
        Сбор до {project.maturityDate ? formatDate(project.maturityDate) : '—'}
      </span>
    </div>
  );

  const renderReservation = (reservation: Reservation) => {
    const money = new MoneyModel(Number(reservation.money.amount), reservation.money.currencyCode);

    return (
      <div className={styles.reservation}>
        <CheckIcon preset="circle-success" size={20} title="Резервирование" />
        <span className={styles.reservationLabel}>
          Создано резервирование на сумму <Money money={money} compact withPennies={false} />
        </span>
      </div>
    );
  };

  const renderCollected = () => (
    <div className={styles.collected}>
      <CheckIcon preset="circle-success" size={20} title="Собран" />
      <span className={styles.collectedLabel}>Собран</span>
    </div>
  );

  const renderAction = () => {
    if (reservation) {
      return renderReservation(reservation);
    }

    if (showInvestButton) {
      return renderInvestAction();
    }

    if (isFullyCollected) {
      return renderCollected();
    }

    return null;
  };

  return (
    <DataTable.Row className={styles.row} onClick={handleRowClick}>
      <DataTable.Cell>
        <CustomLink
          to={RouteDictionary.DETAILED_PROJECT}
          params={{ uuid: project.uuid }}
          className={styles.projectLink}
        >
          <ProjectDebtClass debtClass={project.debtClass} />
          <div className={styles.projectName}>{project.name[0].text}</div>
        </CustomLink>
      </DataTable.Cell>
      <DataTable.Cell className={styles.nowrap}>
        <Money money={project.debtPrice} compact />
      </DataTable.Cell>
      <DataTable.Cell className={styles.nowrap}>
        {project.recoveryForecast ? <Money money={project.recoveryForecast} compact /> : '—'}
      </DataTable.Cell>
      <DataTable.Cell className={styles.nowrap}>{roiIrr}</DataTable.Cell>
      <DataTable.Cell className={styles.nowrap}>
        {isPaidTable ? factTerm : `${project.initialTerm.toFixed(0)} мес.`}
      </DataTable.Cell>
      {isCurrentTable && (
        <>
          <DataTable.Cell className={styles.nowrap}>{startedAt}</DataTable.Cell>
          <DataTable.Cell className={styles.nowrapWide}>{plannedCompletion}</DataTable.Cell>
        </>
      )}
      {isPaidTable && <DataTable.Cell className={styles.nowrap}>{completedAt}</DataTable.Cell>}
      {isRefundedTable && (
        <>
          <DataTable.Cell className={styles.nowrap}>{startedAt}</DataTable.Cell>
          <DataTable.Cell className={styles.nowrap}>{cancelledAt}</DataTable.Cell>
        </>
      )}
      <DataTable.Cell>{progressBar}</DataTable.Cell>
      {!withDateColumns && <DataTable.Cell>{renderAction()}</DataTable.Cell>}
    </DataTable.Row>
  );
};

export default PrimaryMarketTableRow;
